/**
 * Easter-egg billboards: 6–10 roadside billboards and neon signs (Breakpoint, Solana culture, the rare Vibhu code, the
 * Solana on Excel / Solana Baltics credits — words in src/data/easterEggs.ts) that stand on the sidewalks, face the
 * camera and recycle as the city scrolls past.
 *
 * Cheap by construction: every sign is ONE mesh (face + frame + posts merged, frame parts sample solid swatches of the
 * same atlas), all signs share ONE material and two canvas textures (a colour atlas and a half-size glow atlas: neon
 * letters and lamp heads glow, boards carry a faint copy so they read in shade). A new sign = 4 new UVs, nothing else.
 *
 * Space: scene space as GameScene draws it — the camera sits behind the car and looks down +z, so +x is the screen's
 * LEFT. A face turned by π about y has its texture's u running to the screen's right: the words read correctly with no
 * mirroring in the canvas. Sidewalks run from |x| 7.1 to 14.6 m (top at 0.34 m); buildings start at |x| 15.1.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { liveSigns, type SignSpec } from "../data/easterEggs";

export interface Billboards {
  /** moves the signs by speed × dt towards the camera (the city's scroll) and animates them */
  update(dt: number, speed: number): void;
  /** the two halves of update(): call scroll(distance) where the city scrolls, animate(dt) once a frame */
  scroll(distance: number): void;
  animate(dt: number): void;
  dispose(): void;
  readonly root: THREE.Group;
  /** the colour atlas (dev pages) */
  readonly atlas: HTMLCanvasElement;
}

export interface BillboardOptions {
  /** uploads the two atlases at once (no upload hitch when the first sign comes into view) */
  renderer?: THREE.WebGLRenderer;
  random?: () => number;
  /** "today" for the dated signs (Breakpoint, the code) */
  now?: Date;
  /** number of signs (6–10; default 8) */
  count?: number;
  /** dev: fixed signs — slot i shows ids[i] at z[i] (no hidden laps until they recycle) */
  fixed?: { ids: string[]; z: number[] };
  /** where a sign may stand, given the z it would take — GameScene: just in front of a street lamp, so the pole hides behind the
   *  board instead of standing on it (Karolis 2026-10-16: "it's fine as long as it's not exactly on the thing") */
  spot?: (z: number) => number;
}

/** sign marks (SignSpec.logo), loaded once; a sign paints its mark when it has arrived */
const logoImages = new Map<string, HTMLImageElement>();

const CELL_W = 512;
const CELL_H = 256;
const COLS = 4;
const GLOW_SCALE = 0.5;
const FONT = `"Archivo Run", "Arial Black", Impact, sans-serif`;
/** where signs come back: ahead of the car, in the haze */
const RESPAWN_MIN = 180;
const RESPAWN_SPREAD = 80;
/** a sign this far behind the camera's z (−8.8) has left the screen */
const GONE_Z = -18;
/** turned this much towards the road, so the face meets the driver */
const TILT = 0.26;
/** one sign in five stays down for a lap: the street breathes */
const HIDDEN_CHANCE = 0.2;
const MIN_GAP = 30;

/** solid swatches in the atlas's last cell (4 × 2 blocks): frame and post colours, lamp heads */
const SWATCHES = [
  { color: "#2b2838", glow: "#000000" },   // 0 frame
  { color: "#191819", glow: "#000000" },   // 1 dark
  { color: "#8c8aa0", glow: "#000000" },   // 2 metal
  { color: "#fff1cc", glow: "#fff1cc" },   // 3 lamp head (lit)
  { color: "#9945ff", glow: "#9945ff" },   // 4 purple tube
  { color: "#14f195", glow: "#14f195" },   // 5 green tube
  { color: "#d9d4ea", glow: "#000000" },   // 6 light frame
  { color: "#44405a", glow: "#000000" }    // 7 base plate
] as const;

type Family = "board" | "neon";
const familyOf = (sign: SignSpec): Family => (sign.style === "neon" ? "neon" : "board");

interface Slot {
  mesh: THREE.Mesh;
  uv: THREE.BufferAttribute;
  family: Family;
  side: 1 | -1;
  sign?: SignSpec;
}

export function createBillboards(scene: THREE.Scene, options: BillboardOptions = {}): Billboards {
  const random = options.random ?? Math.random;
  const signs = liveSigns(options.now ?? new Date());
  const cells = signs.length + 1;                                  // + the swatch cell
  const rows = Math.ceil(cells / COLS);
  const W = CELL_W * COLS, H = CELL_H * rows;

  const atlas = document.createElement("canvas");
  atlas.width = W;
  atlas.height = H;
  const glow = document.createElement("canvas");
  glow.width = Math.round(W * GLOW_SCALE);
  glow.height = Math.round(H * GLOW_SCALE);

  const cellOf = (index: number) => ({ x: (index % COLS) * CELL_W, y: Math.floor(index / COLS) * CELL_H });
  const swatchCell = cellOf(signs.length);

  const paintAll = (): void => {
    const c = atlas.getContext("2d"), g = glow.getContext("2d");
    if (!c || !g) return;
    c.clearRect(0, 0, W, H);
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = "#000";
    g.fillRect(0, 0, glow.width, glow.height);
    g.setTransform(GLOW_SCALE, 0, 0, GLOW_SCALE, 0, 0);
    signs.forEach((sign, i) => {
      const { x, y } = cellOf(i);
      paintSign(c, sign, x, y, false);
      paintSign(g, sign, x, y, true);
    });
    SWATCHES.forEach((swatch, k) => {
      const x = swatchCell.x + (k % 4) * 128, y = swatchCell.y + Math.floor(k / 4) * 128;
      c.fillStyle = swatch.color;
      c.fillRect(x, y, 128, 128);
      g.fillStyle = swatch.glow;
      g.fillRect(x, y, 128, 128);
    });
  };
  paintAll();

  const map = new THREE.CanvasTexture(atlas);
  const emissiveMap = new THREE.CanvasTexture(glow);
  // the marks (SVG, same origin) arrive after the first paint: repaint once each lands
  for (const src of new Set(signs.map((s) => s.logo).filter((l): l is string => !!l))) {
    if (logoImages.has(src)) continue;
    const img = new Image();
    img.decoding = "async";
    img.onload = () => { logoImages.set(src, img); paintAll(); map.needsUpdate = true; emissiveMap.needsUpdate = true; };
    img.src = `${import.meta.env.BASE_URL}${src}`;
  }
  for (const texture of [map, emissiveMap]) {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
  }
  // the bundled display face may still be on its way: paint again once it is here
  const fonts = typeof document !== "undefined" ? document.fonts : undefined;
  if (fonts && !fonts.check(`900 40px "Archivo Run"`)) {
    void fonts.load(`900 40px "Archivo Run"`).then(() => {
      if (!fonts.check(`900 40px "Archivo Run"`)) return;
      paintAll();
      map.needsUpdate = true;
      emissiveMap.needsUpdate = true;
    }).catch(() => undefined);
  }

  // lit by the sun like the city (Lambert), with its own light: neon letters and lamp heads go over 1 and bloom on high
  const material = new THREE.MeshLambertMaterial({ map, emissive: 0xffffff, emissiveMap, emissiveIntensity: 1.6 });
  options.renderer?.initTexture(map);
  options.renderer?.initTexture(emissiveMap);

  const swatchUv = (k: number): [number, number] => [
    (swatchCell.x + (k % 4) * 128 + 64) / W,
    1 - (swatchCell.y + Math.floor(k / 4) * 128 + 64) / H
  ];
  const part = (w: number, h: number, d: number, x: number, y: number, z: number, swatch: number): THREE.BufferGeometry => {
    const box = new THREE.BoxGeometry(w, h, d).translate(x, y, z);
    const uv = box.getAttribute("uv") as THREE.BufferAttribute;
    const [u, v] = swatchUv(swatch);
    for (let i = 0; i < uv.count; i++) uv.setXY(i, u, v);
    return box;
  };

  /** one sign's geometry, the face first (its four UVs are the ones that change) */
  const geometryFor = (family: Family): THREE.BufferGeometry => {
    const list: THREE.BufferGeometry[] = [];
    if (family === "board") {
      // 6.4 × 3.2 m face, 4 m up: two posts, a catwalk and three lamps
      list.push(new THREE.PlaneGeometry(6.4, 3.2).translate(0, 5.6, 0));
      list.push(part(6.8, 3.6, 0.3, 0, 5.6, -0.17, 0));
      for (const x of [-2.2, 2.2]) list.push(part(0.3, 4.0, 0.3, x, 2.0, -0.3, 2));
      list.push(part(6.6, 0.1, 0.6, 0, 3.75, 0.3, 2));
      for (const x of [-2.2, 0, 2.2]) {
        list.push(part(0.08, 0.08, 0.7, x, 7.55, 0.3, 2));
        list.push(part(0.5, 0.14, 0.24, x, 7.5, 0.66, 3));
      }
    } else {
      // 5.6 × 2.8 m dark sign on one pole, a glowing tube along its top and bottom
      list.push(new THREE.PlaneGeometry(5.6, 2.8).translate(0, 5.2, 0));
      list.push(part(5.9, 3.1, 0.36, 0, 5.2, -0.2, 1));
      list.push(part(0.36, 3.7, 0.36, 0, 1.85, -0.25, 2));
      list.push(part(1.2, 0.12, 1.2, 0, 0.06, -0.25, 7));
      list.push(part(5.9, 0.08, 0.08, 0, 6.72, 0.0, 4));
      list.push(part(5.9, 0.08, 0.08, 0, 3.68, 0.0, 5));
    }
    const merged = mergeGeometries(list, false);
    for (const geometry of list) geometry.dispose();
    if (!merged) throw new Error("easterEggs: billboard geometry did not merge");
    return merged;
  };

  const root = new THREE.Group();
  root.name = "easter-egg-billboards";
  const count = THREE.MathUtils.clamp(options.count ?? options.fixed?.ids.length ?? 8, 1, 10);
  const slots: Slot[] = [];
  for (let i = 0; i < count; i++) {
    const fixedSign = options.fixed ? signs.find((s) => s.id === options.fixed?.ids[i]) : undefined;
    const family: Family = fixedSign ? familyOf(fixedSign) : i % 4 === 1 || i % 4 === 2 ? "neon" : "board";
    const side: 1 | -1 = i % 2 === 0 ? 1 : -1;
    const geometry = geometryFor(family);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = `billboard-${i}`;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    // face towards −z (the camera), turned towards the road: on the left (+x) that is a turn past π, on the right short of it
    mesh.rotation.y = Math.PI + side * TILT;
    // boards: the 6.8 m frame spans |x| 7.9–14.5 (sidewalk 7.1–15.1; buildings' real fronts at 15.1 — 0.6 m clear, 2026-10-17:
    // at 15.09 they went half inside buildings); neon: 8.55–14.25
    mesh.position.set(side * (family === "board" ? 11.2 : 11.4), 0.34, 0);
    root.add(mesh);
    const slot: Slot = { mesh, uv: geometry.getAttribute("uv") as THREE.BufferAttribute, family, side };
    slots.push(slot);
    if (options.fixed && fixedSign) {
      show(slot, fixedSign);
      mesh.position.z = options.fixed.z[i] ?? 40 + i * 30;
    } else {
      putUp(slot, 25 + i * 30 + random() * 10, false);
    }
  }
  scene.add(root);

  function show(slot: Slot, sign: SignSpec): void {
    slot.sign = sign;
    const { x, y } = cellOf(signs.indexOf(sign));
    const inset = 3;
    const u0 = (x + inset) / W, u1 = (x + CELL_W - inset) / W;
    const v1 = 1 - (y + inset) / H, v0 = 1 - (y + CELL_H - inset) / H;
    slot.uv.setXY(0, u0, v1);
    slot.uv.setXY(1, u1, v1);
    slot.uv.setXY(2, u0, v0);
    slot.uv.setXY(3, u1, v0);
    slot.uv.needsUpdate = true;
  }

  /** a weighted pick of the family's signs, not one already standing */
  function pick(family: Family): SignSpec | undefined {
    const standing = new Set(slots.map((s) => s.mesh.visible ? s.sign?.id : undefined));
    let pool = signs.filter((s) => familyOf(s) === family && !standing.has(s.id));
    if (!pool.length) pool = signs.filter((s) => familyOf(s) === family);
    const total = pool.reduce((sum, s) => sum + s.weight, 0);
    let r = random() * total;
    for (const sign of pool) if ((r -= sign.weight) <= 0) return sign;
    return pool[pool.length - 1];
  }

  function putUp(slot: Slot, z: number, mayHide: boolean): void {
    const sign = pick(slot.family);
    slot.mesh.visible = !!sign && !(mayHide && random() < HIDDEN_CHANCE);
    if (sign) show(slot, sign);
    slot.mesh.position.z = options.spot ? options.spot(z) : z;
  }

  let clock = 0;
  const api: Billboards = {
    root,
    atlas,
    scroll(distance: number): void {
      if (!distance) return;
      for (const slot of slots) {
        slot.mesh.position.z -= distance;
        if (slot.mesh.position.z < GONE_Z) {
          let ahead = 0;
          for (const other of slots) if (other !== slot && other.side === slot.side) ahead = Math.max(ahead, other.mesh.position.z);
          putUp(slot, Math.max(RESPAWN_MIN + random() * RESPAWN_SPREAD, ahead + MIN_GAP), true);
        }
      }
    },
    animate(dt: number): void {
      clock += dt;
      // the neon hum: a slow, faint breathing of the light (all signs at once; one uniform)
      material.emissiveIntensity = 1.6 + Math.sin(clock * 2.1) * 0.06;
    },
    update(dt: number, speed: number): void {
      api.scroll(speed * dt);
      api.animate(dt);
    },
    dispose(): void {
      scene.remove(root);
      for (const slot of slots) slot.mesh.geometry.dispose();
      material.dispose();
      map.dispose();
      emissiveMap.dispose();
    }
  };
  return api;
}

/* ---------- painting ---------- */

function setFont(ctx: CanvasRenderingContext2D, size: number, weight = 900): void {
  ctx.font = `${weight} ${Math.round(size)}px ${FONT}`;
  if ("fontStretch" in ctx) ctx.fontStretch = "semi-condensed";
}

/** the largest size up to `max` at which `text` fits `width` */
function fit(ctx: CanvasRenderingContext2D, text: string, width: number, max: number, weight = 900): number {
  setFont(ctx, max, weight);
  const measured = ctx.measureText(text).width;
  return measured <= width ? max : Math.max(10, Math.floor(max * width / measured));
}

/** the hero line: the middle of three, the second of two when the first ends in "=" or ",", else the first */
function heroIndex(lines: string[]): number {
  if (lines.length === 3) return 1;
  if (lines.length === 2 && /[=,]$/.test(lines[0])) return 1;
  return 0;
}

function layoutLines(ctx: CanvasRenderingContext2D, sign: SignSpec, width: number, heroMax: number, restMax: number): { size: number; hero: boolean }[] {
  const big = sign.big ?? [heroIndex(sign.lines)];
  return sign.lines.map((line, i) => {
    const hero = big.includes(i);
    return { size: fit(ctx, line, width, hero ? heroMax : restMax), hero };
  });
}

function drawLines(ctx: CanvasRenderingContext2D, sign: SignSpec, x: number, y: number, w: number, h: number, colors: (hero: boolean, i: number) => string, glowBlur: number): void {
  const single = sign.lines.length === 1;
  const sizes = layoutLines(ctx, sign, w, single ? 170 : 104, 46);
  const gap = 10;
  const total = sizes.reduce((sum, s) => sum + s.size * 0.86, 0) + gap * (sizes.length - 1);
  let cursor = y + (h - total) / 2;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  sign.lines.forEach((line, i) => {
    const { size, hero } = sizes[i];
    cursor += size * 0.86;
    setFont(ctx, size, hero ? 900 : 800);
    const color = colors(hero, i);
    if (glowBlur > 0) {
      ctx.shadowColor = color;
      ctx.shadowBlur = glowBlur;
    }
    ctx.fillStyle = color;
    ctx.fillText(line, x + w / 2, cursor - size * 0.06);
    ctx.shadowBlur = 0;
    cursor += gap;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** One sign into its 512 × 256 cell. `glow` paints the emissive version (neon full, boards a faint copy). */
function paintSign(ctx: CanvasRenderingContext2D, sign: SignSpec, x: number, y: number, glow: boolean): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, CELL_W, CELL_H);
  ctx.clip();
  if (sign.style === "neon") {
    ctx.fillStyle = glow ? "#000" : sign.bg;
    ctx.fillRect(x, y, CELL_W, CELL_H);
    if (!glow) {                                                    // a faint brick-dark texture behind the tubes
      ctx.fillStyle = "rgba(255,255,255,0.035)";
      for (let row = 0; row < 8; row++) for (let col = 0; col < 9; col++) ctx.fillRect(x + col * 60 + (row % 2) * 30, y + row * 32 + 2, 56, 28);
    }
    ctx.lineWidth = 7;
    ctx.strokeStyle = sign.accent;
    ctx.shadowColor = sign.accent;
    ctx.shadowBlur = 14;
    roundRect(ctx, x + 16, y + 16, CELL_W - 32, CELL_H - 32, 26);
    ctx.stroke();
    ctx.shadowBlur = 0;
    drawLines(ctx, sign, x + 40, y + 30, CELL_W - 80, CELL_H - 60, (hero, i) => (hero || sign.lines.length === 1 ? sign.ink : i === 0 ? sign.ink : sign.accent), 16);
    // a hot white core in the hero letters (tubes are whiter in the middle)
    if (!glow) {
      ctx.globalAlpha = 0.35;
      drawLines(ctx, sign, x + 40, y + 30, CELL_W - 80, CELL_H - 60, (hero) => (hero ? "#ffffff" : "rgba(0,0,0,0)"), 0);
      ctx.globalAlpha = 1;
    }
  } else if (sign.logo) {
    // a clean lockup board: the ground, the mark centred, the words underneath — no wedge, no bars (Karolis: "SB and Solana Baltics underneath")
    ctx.fillStyle = sign.bg;
    ctx.fillRect(x, y, CELL_W, CELL_H);
    const mark = 128, top = 24;
    const img = logoImages.get(sign.logo);
    const markW = img?.naturalHeight ? mark * (img.naturalWidth / img.naturalHeight) : mark * 1.17;
    if (img) ctx.drawImage(img, x + (CELL_W - markW) / 2, y + top, markW, mark);
    const textY = y + top + mark + 10;
    drawLines(ctx, sign, x + 40, textY, CELL_W - 80, y + CELL_H - 16 - textY, (hero) => (hero ? sign.ink : sign.accent), 0);
    if (glow) { ctx.fillStyle = "rgba(0,0,0,0.85)"; ctx.fillRect(x, y, CELL_W, CELL_H); }
  } else if (sign.style === "sheet") {
    paintSheet(ctx, sign, x, y);
    if (glow) { ctx.fillStyle = "rgba(0,0,0,0.85)"; ctx.fillRect(x, y, CELL_W, CELL_H); }
  } else {
    ctx.fillStyle = sign.bg;
    ctx.fillRect(x, y, CELL_W, CELL_H);
    // a bold accent wedge and a bottom bar: the Kenney-bright billboard look
    ctx.fillStyle = sign.accent;
    ctx.beginPath();
    ctx.moveTo(x, y + CELL_H);
    ctx.lineTo(x, y + CELL_H - 70);
    ctx.lineTo(x + 70, y + CELL_H);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(x, y + CELL_H - 14, CELL_W, 14);
    ctx.fillRect(x, y, CELL_W, 8);
    drawLines(ctx, sign, x + 34, y + 22, CELL_W - 68, CELL_H - 56, (hero, i) => (hero || i === 0 ? sign.ink : sign.accent), 0);
    if (glow) { ctx.fillStyle = "rgba(0,0,0,0.85)"; ctx.fillRect(x, y, CELL_W, CELL_H); }
  }
  ctx.restore();
}

/** Solana on Excel: the sign is a spreadsheet — column letters, row numbers, gridlines, a selected range with its handle. */
function paintSheet(ctx: CanvasRenderingContext2D, sign: SignSpec, x: number, y: number): void {
  const head = 30, gutter = 40, colW = (CELL_W - gutter) / 6, rowH = (CELL_H - head) / 4;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x, y, CELL_W, CELL_H);
  ctx.fillStyle = "#eef0f2";
  ctx.fillRect(x, y, CELL_W, head);
  ctx.fillRect(x, y, gutter, CELL_H);
  ctx.strokeStyle = "#cfd3d8";
  ctx.lineWidth = 2;
  for (let c = 0; c <= 6; c++) { ctx.beginPath(); ctx.moveTo(x + gutter + c * colW, y); ctx.lineTo(x + gutter + c * colW, y + CELL_H); ctx.stroke(); }
  for (let r = 0; r <= 4; r++) { ctx.beginPath(); ctx.moveTo(x, y + head + r * rowH); ctx.lineTo(x + CELL_W, y + head + r * rowH); ctx.stroke(); }
  ctx.fillStyle = "#5f6670";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  setFont(ctx, 20, 700);
  "ABCDEF".split("").forEach((letter, c) => ctx.fillText(letter, x + gutter + c * colW + colW / 2, y + head / 2 + 1));
  for (let r = 0; r < 4; r++) ctx.fillText(String(r + 1), x + gutter / 2, y + head + r * rowH + rowH / 2 + 1);
  // the selected range B2:F3 (green border, light green fill) and its fill handle
  const sx = x + gutter + colW * 0.0, sy = y + head + rowH * 1, sw = colW * 6, sh = rowH * 2;
  ctx.fillStyle = "rgba(16,124,65,0.08)";
  ctx.fillRect(sx, sy, sw, sh);
  ctx.strokeStyle = sign.accent;
  ctx.lineWidth = 6;
  ctx.strokeRect(sx + 3, sy + 3, sw - 6, sh - 6);
  ctx.fillStyle = sign.accent;
  ctx.fillRect(sx + sw - 14, sy + sh - 14, 14, 14);
  ctx.textBaseline = "alphabetic";
  const big = fit(ctx, sign.lines[0], sw - 40, 76);
  setFont(ctx, big, 900);
  ctx.fillStyle = sign.ink;
  ctx.fillText(sign.lines[0], sx + sw / 2, sy + sh / 2 + big * 0.34);
  if (sign.lines[1]) {
    const small = fit(ctx, sign.lines[1], sw - 40, 34, 800);
    setFont(ctx, small, 800);
    ctx.fillStyle = "#3b4048";
    ctx.textAlign = "left";
    ctx.fillText(sign.lines[1], x + gutter + 10, y + head + rowH * 3 + rowH / 2 + small * 0.36);
    ctx.textAlign = "center";
  }
  // row 1: the formula, Excel-style
  ctx.textAlign = "left";
  setFont(ctx, 24, 700);
  ctx.fillStyle = "#107c41";
  ctx.fillText("=SOL()", x + gutter + 10, y + head + rowH / 2 + 9);
}
